/**
 * Copyright ©2023 Drivepoint
 */

import localforage from "localforage";
import {ObjectUtilities} from "@bainbridge-growth/node-common";
import {Store} from "@bainbridge-growth/node-frontend";
import {StoreEvent} from "@bainbridge-growth/node-frontend/src/store/StoreEvent";
import WebAppServerClient from "../clients/WebAppServerClient";
import {EventBus} from "../eventbus/EventBus";
import State from "../state/State";
import {StateEvent} from "../state/StateEvent";

export type DAZZLER_SCOPES = "USER" | "COMPANY" | "GLOBAL";

export default class DazzlerDocumentsStore extends Store {

  private static WRITE_DELAY_MS: number = 1000;

  protected _company?: any;
  private _writeThroughTimeout: any;
  private _forage: LocalForage = localforage.createInstance({name: "DazzlerDocumentsStore"});
  private _abortController: AbortController;

  init(): void {
    this.loading = true;
    State.register("company", (event: StateEvent) => {
      this.value = [];
      this._company = event.value;
      this.refresh();
    });
    EventBus.register("dazzler:publish", this._onDocumentPublish.bind(this));
    this._flushWriteQueue();
  }

  async refresh(): Promise<void> {
    return this.action(async () => {
      if (!this._company) { return; }
      const documents = await WebAppServerClient.request(`/api/company/${this._company.id}/dazzler`, "GET");
      if (documents) {
        this.value = this._updatePublishedDocuments(documents);
      } else {
        this.value = [];
      }
    });
  }

  async create(document: any, scope: DAZZLER_SCOPES): Promise<any> {
    return this.action(async () => {
      this.dispatchEvent(new StoreEvent("creating", document));
      document = await WebAppServerClient.request(`/api/company/${this._company.id}/dazzler?scope=${scope}`, "POST", document);
      this.refresh()
        .then(() => this.dispatchEvent(new StoreEvent("created", document)))
        .catch(error => {
          const event: any = {type: "toast", toast: {title: "Error", subtitle: error.message, severity: "error", id: "dazzler_refresh_error"}};
          EventBus.dispatch(event);
        });
      return document;
    });
  }

  async duplicate(document: any, scope: DAZZLER_SCOPES): Promise<any> {
    document = ObjectUtilities.clone(document);
    document.id = undefined;
    document.source_id = undefined;
    document.folder = undefined;
    document.path = undefined;
    document.scope = undefined;
    document.acl = undefined;
    document.published_id = undefined;
    document.published = undefined;
    document.serialized = undefined;
    return this.create(document, scope);
  }

  async edit(document: any, force: boolean = false): Promise<any> {
    return this.action(async () => {
      this.dispatchEvent(new StoreEvent("editing", document));
      const query: string = force ? "?force=1" : "";
      const results = await WebAppServerClient.request(`/api/company/${this._company.id}/dazzler/${document.id}/edit${query}`, "POST", document);
      if (force) { this.refresh(); }
      return results;
    });
  }

  async update(document: any): Promise<void> {
    if (!this._company) { return; }
    if (document.type === "folder") { document.children = []; }
    await this.writeThrough(document, "PATCH");
    this.value = this.value
      .map((value: any) => value.id === document.id ? document : value)
      .map((value: any) => ({...value, children: value.children ? [] : undefined}));
  }

  async delete(document: any): Promise<void> {
    if (!this._company) { return; }
    return this.action(async () => {
      await WebAppServerClient.request(`/api/company/${this._company.id}/dazzler/${document.id}`, "DELETE", document);
      this.refresh();
    });
  }

  async publish(document: any, scope: "COMPANY" | "GLOBAL", force: boolean = false): Promise<void> {
    if (!this._company) { return; }
    return this.action(async () => {
      await WebAppServerClient.request(`/api/company/${this._company.id}/dazzler/${document.id}?scope=${scope}${force ? "&force=1" : ""}`, "PUT", document);
      this.refresh();
    });
  }

  async writeThrough(document: any, method: "POST" | "PATCH" | "DELETE"): Promise<void> {
    clearTimeout(this._writeThroughTimeout);
    if (this._abortController) { this._abortController.abort(); }
    this._abortController = new AbortController();
    document = ObjectUtilities.clone(document);
    await this._forage.setItem(document.id, {method: method, document: document});
    this.dispatchEvent(new StoreEvent("dirty", true));
    this._writeThroughTimeout = setTimeout(() => this._flushWriteQueue(), DazzlerDocumentsStore.WRITE_DELAY_MS);
  }

  getDocumentById(id: string): any {
    const document = this.value.find((it: any) => it.id === id);
    if (document) { return ObjectUtilities.clone(document); }
  }

  private async _flushWriteQueue(): Promise<void> {
    return this.action(async () => {
      const keys = await this._forage.keys();
      for (const key of keys) {
        const entry: any = await this._forage.getItem(key);
        try {
          logger.trace(`flushing ${entry.method} request for id ${entry.document.id}`);
          const endpoint = entry.method === "POST" ? `/api/company/${this._company.id}/dazzler` : `/api/company/${this._company.id}/dazzler/${entry.document.id}`;
          await WebAppServerClient.request(endpoint, entry.method, entry.document, this._abortController);
        } catch (error: any) {
          logger.debug(error.name, error.message);
          if (error.name === "AbortError") { break; } // if the request was aborted it means a call was made to writeThrough, so exit and wait for next attempt
          logger.error(entry, error.message);
          this.error = error;
        }
        await this._forage.removeItem(key);
      }
      this.dispatchEvent(new StoreEvent("dirty", false));
    });
  }

  protected _updatePublishedDocuments(documents: any[]): any[] {
    return documents
      .map((document: any) => {
        if (document.published_id) {
          document.published = documents.find((it: any) => it.id === document.published_id)?.source_id === document.id;
        }
        return document;
      });
  }

  private _onDocumentPublish(event: any): void {
    this.value = this.value.map((document: any) => document.published_id ? {...document, published: document.id === event.source_id} : document);
  }

}
